/**
 * Cap-weighted return for index-strip / display (null-safe UI contract).
 * Members missing mcap or window ret are excluded from the weighting; if no
 * complete pair remains the result is `null` (not 0) so empty membership is
 * distinguishable from a flat 0% return. Sector-aggregate wire shape still
 * coalesces to 0 at its own boundary — do not reuse this null contract there.
 */
import type { WindowKey } from '@meridian/contracts';

export interface CapWeightable {
  readonly mcap: number | null;
  readonly ret: Readonly<Record<WindowKey, number | null>>;
}

export interface CapWeightedStats {
  /** Cap-weighted return over complete pairs, or `null` when none remain. */
  readonly ret: number | null;
  /** How many members actually carried weight. */
  readonly weightedCount: number;
  /** How many members were considered. */
  readonly totalCount: number;
}

/**
 * The weighting WITH its coverage.
 *
 * `capWeightedReturn` alone lets a caller attribute a return to a membership it
 * was not computed over — "500 NAMES +12.3%" where three names carried the
 * weight. Reachable on live data (a recent listing has no 3Y return; caps have
 * gaps), and the number stays entirely plausible, so nothing surfaces it.
 * Callers that display a member count should read `weightedCount` too.
 */
export function capWeightedStats(
  members: readonly CapWeightable[],
  window: WindowKey,
): CapWeightedStats {
  const weighted = members.flatMap((m) => {
    const r = m.ret[window];
    const cap = m.mcap;
    if (r === null || cap === null || !Number.isFinite(r) || !Number.isFinite(cap) || cap <= 0) {
      return [];
    }
    return [{ mcap: cap, ret: r }];
  });
  const weight = weighted.reduce((a, m) => a + m.mcap, 0);
  const totalCount = members.length;
  if (weight <= 0) return { ret: null, weightedCount: 0, totalCount };
  return {
    ret: weighted.reduce((a, m) => a + m.ret * m.mcap, 0) / weight,
    weightedCount: weighted.length,
    totalCount,
  };
}

export function capWeightedReturn(
  members: readonly CapWeightable[],
  window: WindowKey,
): number | null {
  return capWeightedStats(members, window).ret;
}
